import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { TaskStatus } from "../types";

interface StatusTab {
  value: TaskStatus | undefined;
  labelKey: string;
  color: string;
  activeColor: string;
}

// Every role sees every status tab — filtering by status is not role-gated
// (2026-08-04 user request). Which *tasks* a user may see is still enforced by
// the server (`task:get:all` scoping); the tabs only narrow that result set.
const TABS: StatusTab[] = [
  {
    value: "NEW",
    labelKey: "newTaskStatus.NEW",
    color: "bg-slate-400",
    activeColor: "bg-slate-500 text-white",
  },
  {
    value: "LEAD_REJECT",
    labelKey: "newTaskStatus.LEAD_REJECT",
    color: "bg-red-400",
    activeColor: "bg-red-500 text-white",
  },
  {
    value: "LEAD_PENDING",
    labelKey: "newTaskStatus.LEAD_PENDING",
    color: "bg-blue-400",
    activeColor: "bg-blue-500 text-white",
  },
  {
    value: "PARTNER_REJECT",
    labelKey: "newTaskStatus.PARTNER_REJECT",
    color: "bg-orange-400",
    activeColor: "bg-orange-500 text-white",
  },
  {
    value: "PARTNER_PENDING",
    labelKey: "newTaskStatus.PARTNER_PENDING",
    color: "bg-indigo-400",
    activeColor: "bg-indigo-500 text-white",
  },
  {
    value: "BILLING_REJECT",
    labelKey: "newTaskStatus.BILLING_REJECT",
    color: "bg-red-400",
    activeColor: "bg-red-500 text-white",
  },
  {
    value: "BILLING_PENDING",
    labelKey: "newTaskStatus.BILLING_PENDING",
    color: "bg-amber-400",
    activeColor: "bg-amber-500 text-white",
  },
  {
    value: "DONE",
    labelKey: "newTaskStatus.DONE",
    color: "bg-emerald-500",
    activeColor: "bg-emerald-500 text-white",
  },
  {
    value: "BILLED",
    labelKey: "newTaskStatus.BILLED",
    color: "bg-cyan-500",
    activeColor: "bg-cyan-500 text-white",
  },
];

interface TaskStatusTabsProps {
  value: TaskStatus | undefined;
  onChange: (status: TaskStatus | undefined) => void;
  statusCounts?: { newStatus: string; count: number }[];
}

export function TaskStatusTabs({
  value,
  onChange,
  statusCounts = [],
}: TaskStatusTabsProps) {
  const { t } = useTranslation();

  // Build a lookup map from the API response
  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of statusCounts) {
      map[item.newStatus] = item.count;
    }
    return map;
  }, [statusCounts]);

  return (
    <div className="flex flex-wrap items-center gap-1 rounded-lg bg-slate-100 p-1">
      {TABS.map((tab) => {
        const isActive = value === tab.value;
        const count = counts[tab.value!] ?? 0;
        return (
          <button
            key={tab.labelKey}
            type="button"
            onClick={() => onChange(tab.value)}
            className={cn(
              "relative flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all",
              isActive
                ? cn(tab.activeColor, "shadow-sm")
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/70",
            )}
          >
            {tab.color && !isActive && (
              <span className={cn("h-2 w-2 rounded-full", tab.color)} />
            )}
            {t(tab.labelKey)}
            <span
              className={cn(
                "ml-1 rounded-full px-1.5 py-0.5 text-xs leading-none",
                isActive
                  ? "bg-white/20 text-inherit"
                  : "bg-slate-200 text-slate-500",
              )}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
